import * as Sentry from '@sentry/react'
import { supabase } from '../lib/supabase'

const API_BASE = import.meta.env.VITE_API_BASE_URL as string | undefined
if (!API_BASE) {
  console.warn('VITE_API_BASE_URL is not set — API calls will fail')
}

export class ApiError extends Error {
  status: number
  retryable: boolean
  body: Record<string, unknown>
  constructor(message: string, status: number, retryable = false, body: Record<string, unknown> = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.retryable = retryable
    this.body = body
  }
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('Not authenticated')
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
    'X-Client-Version': __APP_VERSION__,
  }
}

// ─── 426 Upgrade Required ────────────────────────────────────
type UpgradeListener = (minVersion: string) => void
const upgradeListeners = new Set<UpgradeListener>()

/** Subscribe to 426 Upgrade Required events. Returns unsubscribe function. */
export function onUpgradeRequired(listener: UpgradeListener): () => void {
  upgradeListeners.add(listener)
  return () => upgradeListeners.delete(listener)
}

// ─── GET request cache (30s TTL) ────────────────────────────
const CACHE_TTL = 30_000
interface CacheEntry { promise: Promise<unknown>; resolvedAt: number }
const cache = new Map<string, CacheEntry>()

function isGetRequest(options: RequestInit): boolean {
  return !options.method || options.method === 'GET'
}

/** Remove one or all cached GET responses. Call after mutations. */
export function invalidateCache(path?: string): void {
  if (path) {
    cache.delete(path)
  } else {
    cache.clear()
  }
}

export async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  // Only cache GET requests (no body, method absent or 'GET')
  if (isGetRequest(options)) {
    const existing = cache.get(path)
    if (existing && Date.now() - existing.resolvedAt < CACHE_TTL) {
      return existing.promise as Promise<T>
    }

    const promise = doFetch<T>(path, options)
    // Store immediately to dedupe concurrent in-flight requests
    cache.set(path, { promise, resolvedAt: Date.now() })
    // Only clear cache on real errors — keep cache on 429 to prevent retry storms
    promise.catch((err) => {
      if (!(err instanceof ApiError && err.status === 429)) {
        cache.delete(path)
      }
    })
    return promise
  }

  return doFetch<T>(path, options)
}

async function doFetch<T>(path: string, options: RequestInit): Promise<T> {
  const headers = await getAuthHeaders()
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: { ...headers, ...options.headers },
    })
  } catch {
    // Network-level failure (e.g. iOS "Load failed", offline, DNS error)
    throw new ApiError('Network error — check your connection and try again.', 0, true)
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))

    // 426 Upgrade Required — notify listeners and throw
    if (res.status === 426) {
      const minVersion = body?.error?.minVersion ?? 'unknown'
      for (const listener of upgradeListeners) {
        try { listener(minVersion) } catch { /* ignore listener errors */ }
      }
    }

    const message = body?.error?.message ?? `API error: ${res.status}`
    const err = new ApiError(message, res.status, body?.retryable === true, body)
    // Don't report expected flows or transient upstream infra errors.
    const isExpectedStatus = res.status === 401 || res.status === 402 || res.status === 409 || res.status === 426
    const isUpstreamInfra = res.status >= 500 && (
      body?.retryable === true
        || /upstream connect|connection timeout|SSL handshake|<!DOCTYPE|Service unavailable|SERVICE_UNAVAILABLE/i.test(message)
    )
    if (!isExpectedStatus && !isUpstreamInfra) {
      Sentry.captureException(err, { extra: { path, status: res.status, body } })
    }
    throw err
  }

  return res.json() as Promise<T>
}

export { API_BASE }
